import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@billda/shared-ui';
import { createFolder, getFolderContents } from '../api/filesApi';
import { useUploadTrayStore } from '../stores/uploadTrayStore';
import { useStorageUsage } from './useStorageUsage';
import { folderContentsKeys } from './useFolderContents';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';
import { formatFileSize } from '../components/files/formatFileSize';
import {
  type CollectedFile,
  collectEntries,
  distinctDirPaths,
  splitPath,
} from '../utils/folderUpload';

function isConflict(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === 409
  );
}

/**
 * Return the id of the folder named `name` under `parentFolderId`, creating it
 * if needed. If it already exists the backend replies 409, so we look it up and
 * reuse the existing folder — that makes uploading into an existing tree, or
 * re-dropping the same folder, idempotent.
 */
async function ensureFolder(
  siteId: number,
  parentFolderId: string | undefined,
  name: string
): Promise<string> {
  try {
    const created = await createFolder(siteId, { name, parentFolderId });
    return created.id;
  } catch (error) {
    if (isConflict(error)) {
      const contents = await getFolderContents(siteId, parentFolderId);
      const existing = contents.folders.find(
        f => f.name.toLowerCase() === name.toLowerCase()
      );
      if (existing) {
        return existing.id;
      }
    }
    throw error;
  }
}

/**
 * Resolve every directory path to a server folder id (relative to baseFolderId),
 * creating folders as needed. Paths are processed shallowest-first so a parent is
 * always resolved before its children. A directory whose creation fails is left
 * unmapped so its files can be reported as skipped rather than aborting the whole
 * upload.
 */
async function resolveFolderTree(
  siteId: number,
  baseFolderId: string | undefined,
  dirPaths: string[]
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  for (const dir of dirPaths) {
    const { dir: parentDir, name } = splitPath(dir);
    const parentId = parentDir === '' ? baseFolderId : map.get(parentDir);
    if (parentDir !== '' && parentId === undefined) {
      // Parent failed to resolve — its descendants can't be placed either.
      continue;
    }
    try {
      map.set(dir, await ensureFolder(siteId, parentId, name));
    } catch {
      // Leave unmapped; files under this directory are skipped below.
    }
  }
  return map;
}

type QueueItem = {
  file: File;
  fileName: string;
  siteId: number;
  folderId?: string;
};

/**
 * Upload a set of collected files into the current folder. Files carrying a
 * relative folder path have that folder hierarchy recreated on the server first;
 * flat files (or a plain multi-file selection) are queued directly. Handles the
 * storage pre-flight and per-file size checks shared with single-file uploads.
 */
export function useFolderUpload(siteId: number, folderId?: string) {
  const addItems = useUploadTrayStore(state => state.addItems);
  const { data: storageUsage } = useStorageUsage();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const queryClient = useQueryClient();
  const [isPreparing, setIsPreparing] = useState(false);

  const upload = useCallback(
    async (collected: CollectedFile[]) => {
      if (collected.length === 0) {
        return;
      }

      // Storage pre-flight across every file in the drop/selection.
      if (storageUsage) {
        const totalSize = collected.reduce((sum, c) => sum + c.file.size, 0);
        const remaining =
          storageUsage.maxStorageSizeBytes -
          storageUsage.currentStorageUsageBytes;
        if (totalSize > remaining) {
          showSnackbar(
            `These files total ${formatFileSize(totalSize)} but you only have ${formatFileSize(remaining)} remaining`,
            'error'
          );
          return;
        }
      }

      // Per-file size limit — oversized files are dropped with a warning.
      const valid = collected.filter(c => {
        if (storageUsage && c.file.size > storageUsage.maxFileSizeBytes) {
          showSnackbar(
            `${c.file.name} exceeds the ${formatFileSize(storageUsage.maxFileSizeBytes)} file size limit`,
            'warning'
          );
          return false;
        }
        return true;
      });
      if (valid.length === 0) {
        return;
      }

      const dirPaths = distinctDirPaths(valid);

      // Flat upload — no folders to create.
      if (dirPaths.length === 0) {
        addItems(
          valid.map(c => ({
            file: c.file,
            fileName: c.file.name,
            siteId,
            folderId,
          }))
        );
        return;
      }

      // Folder upload — recreate the hierarchy, then queue files into it.
      setIsPreparing(true);
      try {
        const dirMap = await resolveFolderTree(siteId, folderId, dirPaths);

        const items: QueueItem[] = [];
        let skipped = 0;
        for (const c of valid) {
          const { dir, name } = splitPath(c.path);
          const targetFolderId = dir === '' ? folderId : dirMap.get(dir);
          if (dir !== '' && targetFolderId === undefined) {
            skipped++;
            continue;
          }
          items.push({
            file: c.file,
            fileName: name,
            siteId,
            folderId: targetFolderId,
          });
        }

        // Surface the new folders immediately, before the uploads finish.
        queryClient.invalidateQueries({ queryKey: folderContentsKeys.all });

        if (items.length > 0) {
          addItems(items);
        }
        if (skipped > 0) {
          showSnackbar(
            `${skipped} file${skipped === 1 ? '' : 's'} skipped — a folder couldn't be created`,
            'warning'
          );
        }
      } catch (error) {
        showSnackbar(getErrorMessage(error), 'error');
      } finally {
        setIsPreparing(false);
      }
    },
    [siteId, folderId, storageUsage, showSnackbar, addItems, queryClient]
  );

  // Drag-and-drop path: collect files from dropped folder entries (async
  // traversal) first, surfacing any traversal failure rather than swallowing
  // the drop. walkEntry already skips individual unreadable entries.
  const uploadEntries = useCallback(
    async (entries: FileSystemEntry[]) => {
      // Cover the async folder walk with the preparing overlay too — a large
      // dropped tree can take a moment before upload() takes over the flag.
      setIsPreparing(true);
      try {
        const collected = await collectEntries(entries);
        await upload(collected);
      } catch (error) {
        showSnackbar(getErrorMessage(error), 'error');
      } finally {
        setIsPreparing(false);
      }
    },
    [upload, showSnackbar]
  );

  return { upload, uploadEntries, isPreparing };
}
